import { useState, type ReactNode } from 'react';
import { Button, Stack, Typography } from '@mui/material';
import { DefinitionList, DialogSurface, FormTextField, InlineNotice, LoadingIndicator, StatusBadge, ValueBadge } from '../../design-system';
import { formatAbsenceDate } from './absenceCalendarModel';
import { absenceDayPartLabel } from './absenceDayPart';
import type { AbsenceTimeOffRequest } from './model';
import { conditionFactsSummary } from './absenceRequestConditions';
import { requestStatus } from './statusPresentation';

type SelfAbsenceRequestDetailProps = {
  request: AbsenceTimeOffRequest;
  canCancel: boolean;
  cancellationIntent?: string;
  cancellationApprovalRequired?: boolean;
  busy: boolean;
  loading?: boolean;
  error?: string;
  evidence?: ReactNode;
  onClose: () => void;
  onCancel: (reason: string) => void;
  onRetry?: () => void;
  onResume?: () => void;
  onOpenApproval?: () => void;
  onOpenCancellation?: () => void;
};

export function SelfAbsenceRequestDetail({ request, cancellationIntent, canCancel, cancellationApprovalRequired, busy, loading, error, evidence, onClose, onCancel, onRetry, onResume, onOpenApproval, onOpenCancellation }: SelfAbsenceRequestDetailProps) {
  const [confirming, setConfirming] = useState(false);
  const [cancelReason, setCancelReason] = useState(cancellationIntent ?? '');
  const [attempted, setAttempted] = useState(cancellationIntent !== undefined);
  const approved = request.status === 'APPROVED';
  const actionLabel = !approved ? '신청 철회' : cancellationApprovalRequired === true ? '취소 승인 요청' : cancellationApprovalRequired === false ? '휴가 취소' : '취소 확인';
  const period = request.startDate === request.endDate ? formatAbsenceDate(request.startDate) : `${formatAbsenceDate(request.startDate)} – ${formatAbsenceDate(request.endDate)}`;
  const unit = request.unitOfTime === 'HOURS' ? '시간' : request.unitOfTime === 'MINUTES' ? '분' : '일';
  const createdAt = new Date(request.createdAt);
  const createdDate = Number.isFinite(createdAt.getTime()) ? new Intl.DateTimeFormat('ko-KR', { dateStyle: 'long' }).format(createdAt) : '-';
  return <><DialogSurface open busy={busy} onClose={onClose} title="휴가 신청 상세" closeLabel="신청 상세 닫기" size="compact"
    titleAdornment={<StatusBadge {...requestStatus(request.status, request.cancellationStatus)} />}
    actions={<Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', justifyContent: 'flex-end' }}>
      {onOpenApproval && <Button disabled={busy} onClick={onOpenApproval}>승인 진행 보기</Button>}
      {onOpenCancellation && <Button disabled={busy} onClick={onOpenCancellation}>취소 요청 보기</Button>}
      {canCancel && <Button color="error" variant="outlined" disabled={busy || loading} onClick={() => setConfirming(true)}>{busy ? '처리 중' : actionLabel}</Button>}
      {onResume && <Button variant="contained" disabled={busy || loading || Boolean(error)} onClick={onResume}>이어서 신청</Button>}
    </Stack>}>
    <Stack spacing={2}>
      {loading && <LoadingIndicator label="신청 상세를 불러오는 중입니다." />}
      {error && <InlineNotice severity="error" action={onRetry ? <Button onClick={onRetry}>다시 확인</Button> : undefined}>{error}</InlineNotice>}
      <DefinitionList items={[
        ['휴가 유형', <ValueBadge label={request.timeOffType.descriptor || request.descriptor} />],
        ['기간', period], ['수량', `${Number(request.quantity).toLocaleString('ko-KR')}${unit}`],
        ...(absenceDayPartLabel(request.dayPart) ? [['사용 시간대', absenceDayPartLabel(request.dayPart)] as [string, string]] : []),
        ...conditionFactsSummary(request.conditionFacts),
        ['작성일', createdDate],
        ...(request.reason ? [['신청 메모', <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{request.reason}</Typography>] as [string, ReactNode]] : []),
      ]} />
      {!loading && evidence}
    </Stack>
  </DialogSurface>
    <DialogSurface open={confirming && canCancel} busy={busy} onClose={() => { if (!busy) setConfirming(false); }} title={`${actionLabel} 확인`} size="compact" actions={<Stack direction="row" spacing={1}>
      <Button variant="outlined" disabled={busy} onClick={() => setConfirming(false)}>돌아가기</Button>
      <Button color="error" variant="contained" disabled={busy || approved && !cancelReason.trim()} onClick={() => { setAttempted(true); onCancel(cancelReason); }}>{attempted && error ? '같은 요청 다시 시도' : actionLabel}</Button>
    </Stack>}>
      <Stack spacing={2}>
        {error && <InlineNotice severity="error">{error}</InlineNotice>}
        <DefinitionList items={[["휴가", request.timeOffType.descriptor || request.descriptor], ['기간', period], ['수량', `${Number(request.quantity).toLocaleString('ko-KR')}${unit}`]]} />
        <Typography variant="body2">{!approved ? '신청을 철회합니다. 승인 대기 중인 사용량이 해제됩니다.' : cancellationApprovalRequired === true ? '취소 승인이 완료되면 사용 기록이 취소되고 차감한 수량이 복원됩니다. 승인 전까지 기존 휴가는 유지됩니다.' : cancellationApprovalRequired === false ? '사용 기록이 즉시 취소되고 차감한 수량이 복원됩니다.' : '현재 정책에 따라 즉시 취소되거나 취소 승인을 요청합니다.'}</Typography>
        {approved && <Typography variant="caption" color="text.secondary">만료된 잔액은 복원되어도 사용 가능량에 포함되지 않을 수 있습니다.</Typography>}
        <FormTextField label={approved ? '취소 사유' : '철회 사유 (선택)'} required={approved} multiline minRows={2} value={cancelReason} disabled={busy || attempted} onChange={(event) => setCancelReason(event.target.value)} slotProps={{ htmlInput: { maxLength: 4000 } }} />
      </Stack>
    </DialogSurface>
  </>;
}
